import React from "react";
import { motion } from "framer-motion";

/**
 * k/ai logo mark for Keimyung AI Research Center.
 *
 * Renders a compact monogram tile with "k/ai" — the slash uses
 * the brand accent (cyan) to visually divide Keimyung × AI.
 *
 * variant: "dark"  → for use on light backgrounds (nav, light pages)
 *          "light" → for use on dark backgrounds (hero, dark pages)
 */
const Logo = ({ size = 40, variant = "dark", className = "", showWordmark = false }) => {
  const isDark = variant === "dark";
  const bg = isDark ? "#0f0f10" : "rgba(255,255,255,0.10)";
  const stroke = isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.25)";
  const fg = isDark ? "#ffffff" : "#ffffff";
  const accent = "#00BCD4";

  return (
    <div className={`inline-flex items-center gap-3 ${className}`}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="k/ai logo"
        data-testid="kai-logo"
        style={{ flexShrink: 0 }}
      >
        {/* Tile */}
        <rect
          x="0.5"
          y="0.5"
          width="63"
          height="63"
          rx="14"
          ry="14"
          fill={bg}
          stroke={stroke}
          strokeWidth="1"
        />

        {/* AI sparkle — primary star (top-right), slowly rotating */}
        <motion.g
          animate={{ rotate: 360 }}
          transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: "53px 13px" }}
        >
          <path
            d="M 53 6 L 55 11 L 60 13 L 55 15 L 53 20 L 51 15 L 46 13 L 51 11 Z"
            fill={accent}
          />
        </motion.g>

        {/* AI sparkle — companion star (upper-left of big star), blinking */}
        <motion.path
          d="M 40 4 L 41 6.5 L 43.5 8 L 41 9.5 L 40 12 L 39 9.5 L 36.5 8 L 39 6.5 Z"
          fill={accent}
          animate={{ opacity: [0.35, 0.95, 0.35] }}
          transition={{
            duration: 2.6,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.4,
          }}
        />

        {/* Monogram k/ai */}
        <text
          x="32"
          y="44"
          textAnchor="middle"
          fontFamily="ui-monospace, SFMono-Regular, 'JetBrains Mono', 'Menlo', monospace"
          fontSize="22"
          fontWeight="600"
          letterSpacing="-1"
          fill={fg}
        >
          k
          <tspan fill={accent} dx="-0.5">
            /
          </tspan>
          <tspan dx="-1.5">ai</tspan>
        </text>
      </svg>

      {showWordmark && (
        <span
          className={`text-base sm:text-xl font-light tracking-tight ${
            isDark ? "text-gray-900" : "text-white"
          }`}
        >
          Keimyung AI Research Center
        </span>
      )}
    </div>
  );
};

export default Logo;
